// Import utility functions for converting 3D data to 2D projections
import { projectSegmentTo2D } from "../Calculations/Polygon2dConversions";
import { projectFeatureTo2D } from "../Calculations/Feature2dConversions";
import { DXF_VARS, POLYGONS } from "../../Global/variables";
import { projectDormerTo2D } from "../Calculations/Dormer2dConversion";
import { getVerticesData } from "../../DataUtils/SavaData";

/**
 * Converts various 3D features related to a roof segment into their 2D projections
 * Used for exporting, analysis, or DXF output
 *
 * @param {Object} segment - The main roof segment
 * @param {Array} setbacks - Array of setback shapes/vertices
 * @param {Array} obstruction - Array of obstacles (e.g., chimneys, vents)
 * @param {Array} panels - Array of solar or roof panels
 * @param {number} segmentIndex - Index of the current segment in the POLYGONS array
 * @returns {Object} 2D projected data for each feature
 */
export function get2dData(segment, setbacks, obstruction, panels, segmentIndex) {
    // Project the segment shape to 2D
    let segment2dPoints = projectSegmentTo2D(segment);

    // Prepare containers for other 2D feature data
    let setback2dPoints = null;
    let obstacle2dPoints = [];
    let panel2dPoints = [];
    let dormerData = [];

    // Convert setbacks to 2D, if present
    if (setbacks.length > 0) {
        let setbackShape = [];
        setbacks.forEach((sb) => {
            setbackShape.push(sb.vertex); // Collect vertices of each setback
        });
        setback2dPoints = projectFeatureTo2D({ polygon: setbackShape }); // Project full setback shape
    }

    // Convert all obstructions (e.g., skylights) to 2D
    if (obstruction.length > 0) {
        obstruction.forEach((obs) => {
            obstacle2dPoints.push({ polygon: projectFeatureTo2D(obs) });
        });
    }

    // Convert panels to 2D
    if (panels.length > 0) {
        panels.forEach((panel) => {
            panel2dPoints.push({ polygon: projectFeatureTo2D(panel) });
        });
    }

    // Convert dormers to 2D if any are associated with the segment
    if (DXF_VARS.dormerData.length > 0) {
        DXF_VARS.dormerData[segmentIndex].forEach((childData) => {
            // Extract raw vertex array from geometry
            let array = POLYGONS[childData].shape.geometry.attributes.position.array;
            let vertices = getVerticesData(array); // Convert raw array to vertex objects
            dormerData.push(projectDormerTo2D(vertices)); // Project dormer geometry to 2D
        });
    }

    // Return a structured object with all 2D projections
    return {
        segment2dPoints,
        setback2dPoints,
        obstacle2dPoints,
        panel2dPoints,
        dormerData
    };
}
